<template>
    <div class="magazine-post">
        <template v-if="post">
            <div class="mag-wrap mag-wrap--narrow mag-alt-bg">
                <MagazineBreadcrumbs :current-name="post.title" :parent="post.category" class="magazine__breadcrumbs" />
            </div>
            <section class="mag-section magazine-post__title-section">
                <div class="mag-wrap mag-wrap--narrow">
                    <PostTitleSection
                        :title="post.title"
                        :author="post.author"
                        :date="post.date"
                        :tags="post.tags"
                        :read-time="getReadingTimeMinutes(post.readingTimeSec)"
                    />
                </div>
            </section>
            <section class="mag-section mag-breakout magazine-post__header-img-section">
                <div class="mag-wrap mag-wrap--narrow">
                    <img :src="post.image" alt="articleImage" class="magazine-post__header-img" />
                </div>
            </section>
            <section
                v-if="post.tableOfContents.enabled"
                class="mag-section magazine-post__extra-narrow-container magazine-post__toc-section"
            >
                <div class="mag-wrap">
                    <TableOfContents
                        :title="post.tableOfContents.title"
                        :show-h3="post.tableOfContents.show_h3"
                        :excluded-ids="excludedTableOfContentsIds"
                        limit-class="cms-content"
                    />
                </div>
            </section>
            <WpContent :html="post.content" class="mag-container cms-content" />
            <section class="mag-section mag-alt-bg">
                <div class="mag-wrap mag-wrap--narrow">
                    <PostList
                        v-if="relatedPosts.length"
                        :received-posts="relatedPosts"
                        :settings="postListSettings"
                        class="magazine-post__related-posts"
                        >{{ $t('magazine.related_posts.heading') }}</PostList
                    >
                    <PostList
                        v-if="latestPosts.length"
                        :received-posts="latestPosts"
                        :settings="postListSettings"
                        class="magazine-post__latest-posts"
                        >{{ $t('magazine.latest_posts.heading') }}</PostList
                    >
                </div>
            </section>
        </template>
        <LoadingIndicator v-else class="magazine-post__loading" />
    </div>
</template>

<router>
{
    meta: {
        hideGettingStartedProgressBar: true
    }
}
</router>

<script>
import allLocales from '~/config/locales.json';
import { actionTypes as navigationActions } from '~/store/navigation';
import magazineHelpersMixin from '~/mixins/magazineHelpersMixin';
import CmsService from '~/services/CmsService';
import MagazineBreadcrumbs from '~/components/magazine/MagazineBreadcrumbs.vue';
import PostTitleSection from '~/components/magazine/PostTitleSection.vue';
import TableOfContents from '~/components/TableOfContents.vue';
import WpContent from '~/components/WpContent.vue';
import PostList from '~/components/magazine/PostList.vue';
import LoadingIndicator from '~/components/LoadingIndicator.vue';

const NOT_FOUND_ERROR = '404 page not found';

export default {
    components: {
        MagazineBreadcrumbs,
        PostTitleSection,
        TableOfContents,
        WpContent,
        PostList,
        LoadingIndicator,
    },
    mixins: [magazineHelpersMixin],
    layout: 'magazine',
    async asyncData(context) {
        const { slug } = context.route.params;
        const cmsService = new CmsService(context);

        try {
            const post = await cmsService.getPostWithSlug(slug);
            const translatedPosts = await cmsService.getPostsWithIds(Object.values(post.translations), 'posts');

            // Setup dynamic language switcher links for articles
            const routeParams = {};
            const translationIdsByLang = post.translations;

            allLocales.forEach(locale => {
                const lang = new Intl.Locale(locale.iso).language;
                const translatedPost = translatedPosts.find(p => p.id === translationIdsByLang[lang]);

                if (context.i18n.locale === locale.code) {
                    return;
                }

                if (!translatedPost) {
                    // Translation not published for this article, point lang switcher link to Magazine front page
                    routeParams[locale.code] = { name: 'magazine' };

                    return;
                }

                routeParams[locale.code] = { slug: translatedPost.slug, fullUrl: translatedPost.link };
            });

            await context.store.dispatch(navigationActions.SET_ROUTE_PARAMS, { routeParams, i18n: context.i18n });

            return { post };
        } catch (error) {
            if (error?.message === NOT_FOUND_ERROR) {
                context.app.$sentry.captureException(error);
            }
        }
    },
    data() {
        return {
            post: null,
            slug: this.$route.params.slug,
            relatedPosts: [],
            latestPosts: [],
            postListSettings: {
                show_author: false,
                show_date: false,
                show_cat: true,
                show_tags: true,
            },
            excludedTableOfContentsIds: ['newsletterTitle'],
        };
    },
    head() {
        if (!this.post?.seoMeta) {
            return {};
        }

        const postMapping = {
            ...this.baseMetaMapping,
            ...this.ogImageMapping,
            article_published_time: { hid: 'article-published-time', property: 'article:published_time' },
            article_modified_time: { hid: 'article-modified-time', property: 'article:modified_time' },
            author: { hid: 'author', name: 'author' },
        };

        const meta = this.generateMeta(this.post.seoMeta, postMapping);

        return {
            title: this.post.seoMeta.og_title,
            meta,
        };
    },
    mounted() {
        this.fetchRelatedPosts();
    },
    methods: {
        async fetchRelatedPosts() {
            try {
                const cmsService = new CmsService(this.$nuxt.context);
                const response = await cmsService.getRecommendedPosts(this.post.id);
                this.relatedPosts = response.relatedPosts;
                this.latestPosts = response.latestPosts;
            } catch (error) {
                this.$sentry.captureException(error);
            }
        },
    },
    nuxtI18n: {
        paths: {
            se: '/magasin/:slug?',
            'fi-sv': '/magasin/:slug?',
            en: '/magazine/:slug?',
            de: '/magazin/:slug?',
            at: '/magazin/:slug?',
            fi: '/lehti/:slug?',
            no: '/magasin/:slug?',
            dk: '/magasin/:slug?',
            ch: '/magazin/:slug?',
            fr: '/magazine/:slug?',
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

// every tag within WP content that does not have class 'mag-breakout' has limited width and is centered
.magazine-post {
    .mag-container {
        padding: 0 20px;

        @include md {
            padding: 0;

            > :not(.mag-breakout) {
                width: 680px;
                margin-left: auto;
                margin-right: auto;
            }
        }
    }
}
</style>

<style lang="scss" scoped>
@import '@/sass/_variables.scss';

.magazine-post {
    &__title-section {
        margin-top: $sm-magazine-spacing;
    }

    &__header-img-section {
        margin-top: $md-magazine-spacing;

        .mag-wrap {
            max-width: unset;
            padding: 0;
        }
    }

    &__header-img {
        width: 100%;
        height: 100%;
        object-fit: cover;

        @include lg {
            border-radius: 20px;
        }
    }

    &__extra-narrow-container {
        @include md {
            width: 680px;
            margin-left: auto;
            margin-right: auto;

            .mag-wrap {
                padding: 0;
            }
        }
    }

    &__toc-section {
        padding-bottom: $md-magazine-spacing;
        border-bottom: 1px solid $gray;

        @include lg {
            padding-bottom: $lg-magazine-spacing;
        }
    }

    &__related-posts {
        padding-bottom: $lg-magazine-spacing;
        border-bottom: 1px solid $primary;
    }

    &__latest-posts {
        padding-top: $md-magazine-spacing;
    }
}
</style>
